import { createContext, useContext, useEffect, useState, ReactNode, useRef, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { AUTO_LOGOUT_DURATIONS, DEFAULT_LOGOUT_DURATION_KEY, LAST_ACTIVITY_TIMESTAMP_KEY } from '@/lib/auth/constants';
import { signOut } from '@/lib/auth/sign-out';
import type { AccessLevel } from '@/types';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { UAParser } from 'ua-parser-js';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  isEmployee: boolean;
  isEmployeeLoading: boolean;
  employeeAccessLevel: AccessLevel | null;
  isAdminEmployee: boolean;
  signInWithGoogle: () => Promise<void>;
  signUpWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signUpWithApple: () => Promise<void>;
  selectedLogoutDuration: keyof typeof AUTO_LOGOUT_DURATIONS;
  setSelectedLogoutDuration: (duration: keyof typeof AUTO_LOGOUT_DURATIONS) => void;
  timeTrackingEnabled: boolean;
  setTimeTrackingEnabled: (enabled: boolean) => void;
  timeTrackingDataSource: 'internal' | 'dotypos' | 'hybrid';
  setTimeTrackingDataSource: (source: 'internal' | 'dotypos' | 'hybrid') => void;
  // Combined time tracking check that considers both business and local state
  isTimeTrackingEnabled: () => boolean;
  // MFA state
  requiresMFA: boolean;
  mfaFactorId: string | null;
  completeMFAChallenge: (code: string) => Promise<boolean>;
  cancelMFAChallenge: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  isEmployee: false,
  isEmployeeLoading: false,
  employeeAccessLevel: null,
  isAdminEmployee: false,
  signInWithGoogle: async () => {},
  signUpWithGoogle: async () => {},
  signInWithApple: async () => {},
  signUpWithApple: async () => {},
  selectedLogoutDuration: DEFAULT_LOGOUT_DURATION_KEY,
  setSelectedLogoutDuration: () => {},
  timeTrackingEnabled: true,
  setTimeTrackingEnabled: () => {},
  timeTrackingDataSource: 'internal',
  setTimeTrackingDataSource: () => {},
  isTimeTrackingEnabled: () => true,
  // MFA defaults
  requiresMFA: false,
  mfaFactorId: null,
  completeMFAChallenge: async () => false,
  cancelMFAChallenge: async () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEmployee, setIsEmployee] = useState(false);
  const [isEmployeeLoading, setIsEmployeeLoading] = useState(false);
  const [employeeAccessLevel, setEmployeeAccessLevel] = useState<AccessLevel | null>(null);
  const [selectedLogoutDuration, setSelectedLogoutDuration] = useState<keyof typeof AUTO_LOGOUT_DURATIONS>(
    (localStorage.getItem('autoLogoutDuration') as keyof typeof AUTO_LOGOUT_DURATIONS) || DEFAULT_LOGOUT_DURATION_KEY
  );
  const [timeTrackingEnabled, setTimeTrackingEnabled] = useState<boolean>(
    localStorage.getItem('timeTrackingEnabled') !== 'false' // Default to true
  );
  const [timeTrackingDataSource, setTimeTrackingDataSource] = useState<'internal' | 'dotypos' | 'hybrid'>(
    (localStorage.getItem('timeTrackingDataSource') as 'internal' | 'dotypos' | 'hybrid') || 'internal'
  );

  // MFA state
  const [requiresMFA, setRequiresMFA] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);

  // Track if device has been registered for current session
  const deviceRegisteredRef = useRef<string | null>(null);
  // Track if MFA has been checked for current session
  const mfaCheckedRef = useRef<string | null>(null);

  // Get a stable device ID that persists across browser sessions
  // This prevents the same browser from appearing as multiple devices
  const getStableDeviceId = async (): Promise<string> => {
    const STORAGE_KEY = 'taginn_device_id';
    let deviceId = localStorage.getItem(STORAGE_KEY);

    if (!deviceId) {
      // Generate new ID using FingerprintJS + random component for uniqueness
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      // Combine fingerprint with random UUID segment to ensure uniqueness
      // while still being stable for this browser instance
      deviceId = `${result.visitorId}-${crypto.randomUUID().slice(0, 8)}`;
      localStorage.setItem(STORAGE_KEY, deviceId);
    }

    return deviceId;
  };

  // Register device on login
  const registerDevice = async (currentSession: Session) => {
    // Skip if already registered for this session
    if (deviceRegisteredRef.current === currentSession.access_token) {
      return;
    }

    // getSession() trusts local storage. A revoked session still looks logged in,
    // and register-device then returns 401. getUser() deletes that token.
    const { error: userError } = await supabase.auth.getUser();
    if (userError) return;

    try {
      // Get stable device ID from localStorage (or generate new one)
      const fingerprint = await getStableDeviceId();

      // Parse user agent
      const parser = new UAParser();
      const uaResult = parser.getResult();

      // Determine device type
      let deviceType: 'desktop' | 'mobile' | 'tablet' = 'desktop';
      const deviceTypeFromUA = uaResult.device.type;
      if (deviceTypeFromUA === 'mobile') {
        deviceType = 'mobile';
      } else if (deviceTypeFromUA === 'tablet') {
        deviceType = 'tablet';
      }

      const browserName = uaResult.browser.name || 'Unknown Browser';
      const osName = uaResult.os.name || 'Unknown OS';
      const deviceName = `${browserName} on ${osName}`;

      const deviceInfo = {
        device_fingerprint: fingerprint,
        device_name: deviceName,
        device_type: deviceType,
        browser_name: browserName,
        browser_version: uaResult.browser.version || '',
        os_name: osName,
        os_version: uaResult.os.version || '',
      };

      // Register with edge function
      const { error } = await supabase.functions.invoke('register-device', {
        body: {
          deviceInfo,
          // session_id claim identifies the auth session (needed to revoke a single device later)
          sessionId: JSON.parse(atob(currentSession.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).session_id,
        }
      });

      if (error) {
        console.error('Failed to register device:', error);
      } else {
        deviceRegisteredRef.current = currentSession.access_token;
        console.log('Device registered successfully');
      }
    } catch (error) {
      console.error('Error registering device:', error);
    }
  };

  // Check if MFA is required after login
  const checkMFARequired = async (currentSession: Session) => {
    // Skip if already checked for this session
    if (mfaCheckedRef.current === currentSession.access_token) {
      return;
    }

    try {
      const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      if (aalError) {
        console.error('Error checking MFA level:', aalError);
        return;
      }

      // If current level is aal1 but next level is aal2, MFA is required
      if (aalData.currentLevel === 'aal1' && aalData.nextLevel === 'aal2') {
        // Get the first verified TOTP factor
        const { data: factorsData } = await supabase.auth.mfa.listFactors();
        const verifiedFactor = factorsData?.totp?.find(f => f.status === 'verified');

        if (verifiedFactor) {
          console.log('MFA required - showing challenge dialog');
          setMfaFactorId(verifiedFactor.id);
          setRequiresMFA(true);
          return;
        }
      }

      // MFA not required or already at aal2
      setRequiresMFA(false);
      setMfaFactorId(null);
      mfaCheckedRef.current = currentSession.access_token;
    } catch (error) {
      console.error('Error checking MFA:', error);
    }
  };

  // Complete MFA challenge with TOTP code
  const completeMFAChallenge = useCallback(async (code: string): Promise<boolean> => {
    if (!mfaFactorId) {
      console.error('No MFA factor ID available');
      return false;
    }

    try {
      // Create challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: mfaFactorId,
      });

      if (challengeError || !challengeData) {
        console.error('Failed to create MFA challenge:', challengeError);
        return false;
      }

      // Verify with code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challengeData.id,
        code,
      });

      if (verifyError) {
        console.error('MFA verification failed:', verifyError);
        return false;
      }

      // Success - clear MFA requirement
      setRequiresMFA(false);
      setMfaFactorId(null);
      if (session) {
        mfaCheckedRef.current = session.access_token;
      }

      console.log('MFA verification successful');
      return true;
    } catch (error) {
      console.error('Error completing MFA challenge:', error);
      return false;
    }
  }, [mfaFactorId, session]);

  // Cancel MFA challenge (signs out user)
  const cancelMFAChallenge = useCallback(async () => {
    console.log('MFA challenge cancelled - signing out');
    setRequiresMFA(false);
    setMfaFactorId(null);
    mfaCheckedRef.current = null;
    await signOut();
  }, []);

  useEffect(() => {
    const employeeCheckCache: { [userId: string]: boolean } = {};

    // Failsafe timeout to prevent infinite loading
    const failsafeTimeout = setTimeout(() => {
      console.warn("Auth loading timeout - forcing reset");
      setLoading(false);
    }, 5000);

    const checkUserRole = async (currentUser: User | null) => {

      if (!currentUser) {
        setIsEmployee(false);
        setIsEmployeeLoading(false);
        setEmployeeAccessLevel(null);
        return;
      }

      // Check cache first
      if (employeeCheckCache[currentUser.id] !== undefined) {
        setIsEmployee(employeeCheckCache[currentUser.id]);
        setIsEmployeeLoading(false);
        return;
      }

      // Set loading state before async operation
      setIsEmployeeLoading(true);

      try {

        // Find the person record, then read access_level from any assignment
        const queryPromise = supabase
          .from('employees')
          .select('id, assignments:employee_assignments(access_level)')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Employee check timeout')), 3000)
        );

        const result = await Promise.race([queryPromise, timeoutPromise]) as any;

        if (result.error) {
          // Handle all errors gracefully - user is not an employee or access denied
          // This includes 406 Not Acceptable (RLS policy) and PGRST116 (no rows found)
          console.log('[AuthContext] Employee check result - user is NOT an employee:', result.error.message);
          setIsEmployee(false);
          setIsEmployeeLoading(false);
          setEmployeeAccessLevel(null);
          employeeCheckCache[currentUser.id] = false;
          return;
        }

        const isEmployeeResult = !!result.data;
        // Use access_level from first assignment (multi-business switching handled elsewhere)
        const accessLevel = (result.data?.assignments?.[0]?.access_level as AccessLevel | undefined) ?? undefined;
        console.log('[AuthContext] Employee check result:', {
          userId: currentUser.id,
          isEmployee: isEmployeeResult,
          accessLevel,
        });
        setIsEmployee(isEmployeeResult);
        setEmployeeAccessLevel(accessLevel ?? null);
        setIsEmployeeLoading(false);
        employeeCheckCache[currentUser.id] = isEmployeeResult;
      } catch (error) {
        setIsEmployee(false);
        setIsEmployeeLoading(false);
        setEmployeeAccessLevel(null);
        employeeCheckCache[currentUser.id] = false;
      }
    };

    const fetchSession = async () => {

      try {
        // Check if the session has expired according to auto-logout duration
        // This runs before AutoLogout mounts, so the timestamp can't be reset first
        const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_TIMESTAMP_KEY);
        const savedDuration = (localStorage.getItem('autoLogoutDuration') as keyof typeof AUTO_LOGOUT_DURATIONS) || DEFAULT_LOGOUT_DURATION_KEY;
        const durationMs = AUTO_LOGOUT_DURATIONS[savedDuration];

        if (lastActivityStr && durationMs > 0) {
          const elapsed = Date.now() - parseInt(lastActivityStr, 10);
          if (elapsed > 0 && elapsed >= durationMs) {
            console.log(`AuthContext: Session expired (inactive ${elapsed}ms, threshold ${durationMs}ms) - signing out`);
            localStorage.removeItem(LAST_ACTIVITY_TIMESTAMP_KEY);
            await signOut();
            toast.info('You were signed out due to inactivity.', { duration: Infinity });
            setSession(null);
            setUser(null);
            setIsEmployee(false);
            clearTimeout(failsafeTimeout);
            setLoading(false);
            return;
          }
        }

        const { data, error } = await supabase.auth.getSession();

        if (error) {
          setSession(null);
          setUser(null);
          setIsEmployee(false);
        } else if (data && data.session) {
          // Same revoked-session case as registerDevice. Check before setSession,
          // which would otherwise put the dead token back after getUser() signs out.
          const { error: userError } = await supabase.auth.getUser();
          if (userError?.name === 'AuthSessionMissingError') {
            setSession(null);
            setUser(null);
            setIsEmployee(false);
          } else {
            setSession(data.session);
            setUser(data.session.user);
            // Now check if user is an employee
            await checkUserRole(data.session.user);
            // Check if MFA is required
            await checkMFARequired(data.session);
            // Register device
            registerDevice(data.session);
          }
        } else {
          setSession(null);
          setUser(null);
          setIsEmployee(false);
        }

        clearTimeout(failsafeTimeout); // Clear failsafe on successful completion
        setLoading(false);
      } catch (error) {
        clearTimeout(failsafeTimeout);
        setSession(null);
        setUser(null);
        setIsEmployee(false);
        setLoading(false);
      }
    };

    fetchSession();

    // Track previous session to avoid unnecessary re-renders on token refresh
    let previousAccessToken: string | null = null;

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        const currentAccessToken = currentSession?.access_token ?? null;

        // Skip non-critical auth events if session hasn't meaningfully changed
        // or if tab just became visible (prevents cascade re-renders)
        // IMPORTANT: Never skip critical auth events like SIGNED_IN, SIGNED_OUT, PASSWORD_RECOVERY, USER_UPDATED
        const criticalEvents = ['SIGNED_IN', 'SIGNED_OUT', 'PASSWORD_RECOVERY', 'USER_UPDATED', 'MFA_CHALLENGE_VERIFIED'];
        const isCriticalEvent = criticalEvents.includes(event);

        if (!isCriticalEvent) {
          // During visibility window, skip ALL non-critical events to prevent re-render storm
          // Track the token but don't trigger state updates
          if (shouldSkipInvalidation()) {
            previousAccessToken = currentAccessToken; // Track token but don't re-render
            return;
          }
          // Outside window, skip if token unchanged
          if (currentAccessToken === previousAccessToken) {
            return;
          }
        }
        previousAccessToken = currentAccessToken;

        // For SIGNED_OUT, always clear session state immediately
        // This avoids stale closure issues where the comparison might not detect the change
        if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
          setIsEmployee(false);
          setRequiresMFA(false);
          setMfaFactorId(null);
          mfaCheckedRef.current = null;
          deviceRegisteredRef.current = null;
          return; // Exit early, no need for further processing
        }

        // Only update state if session has meaningful changes (not just token refresh)
        // This prevents re-renders from token refreshes where user data is identical
        const hasSessionMeaningfullyChanged = (
          oldSession: Session | null,
          newSession: Session | null
        ): boolean => {
          if (!oldSession && !newSession) return false;
          if (!oldSession || !newSession) return true;
          return (
            oldSession.user?.id !== newSession.user?.id ||
            oldSession.user?.email !== newSession.user?.email ||
            oldSession.user?.role !== newSession.user?.role
          );
        };

        if (hasSessionMeaningfullyChanged(session, currentSession)) {
          setSession(currentSession);
          setUser(currentSession?.user || null);
        }

        if (currentSession?.user) {
          // Skip device registration and MFA check during visibility window (tab switch)
          // These only need to run on actual sign-in, not reconnection
          if (!shouldSkipInvalidation()) {
            // Check employee role for new sessions, but don't block on it
            checkUserRole(currentSession.user).catch(error => {
            });
            // Check if MFA is required (important for OAuth logins)
            checkMFARequired(currentSession);
            // Register device on login
            registerDevice(currentSession);
          }
        } else {
          setIsEmployee(false);
          // Clear MFA state on logout
          setRequiresMFA(false);
          setMfaFactorId(null);
          mfaCheckedRef.current = null;
        }
      }
    );

    return () => {
      clearTimeout(failsafeTimeout);
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('autoLogoutDuration', selectedLogoutDuration);
  }, [selectedLogoutDuration]);

  useEffect(() => {
    localStorage.setItem('timeTrackingEnabled', timeTrackingEnabled.toString());
  }, [timeTrackingEnabled]);

  useEffect(() => {
    localStorage.setItem('timeTrackingDataSource', timeTrackingDataSource);
  }, [timeTrackingDataSource]);

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/auth',
        },
      });
      if (error) throw error;
    } catch (error: any) {
      console.warn('Failed to clear employee check cache:', error);
    }
  };

  const signUpWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/auth',
        },
      });
      if (error) throw error;
    } catch (error: any) {
      console.warn('Failed to sign up with Google:', error);
    }
  };

  const signInWithApple = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: window.location.origin + '/auth',
        },
      });
      if (error) throw error;
    } catch (error: any) {
      console.warn('Failed to sign in with Apple:', error);
    }
  };

  const signUpWithApple = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: window.location.origin + '/auth',
        },
      });
      if (error) throw error;
    } catch (error: any) {
      console.warn('Failed to sign up with Apple:', error);
    }
  };

  // Memoize context value to prevent consumer re-renders from reference changes
  const contextValue = useMemo<AuthContextType>(() => ({
    session,
    user,
    loading,
    isEmployee,
    isEmployeeLoading,
    employeeAccessLevel,
    isAdminEmployee: isEmployee && employeeAccessLevel === 'admin',
    signInWithGoogle,
    signUpWithGoogle,
    signInWithApple,
    signUpWithApple,
    selectedLogoutDuration,
    setSelectedLogoutDuration,
    timeTrackingEnabled,
    setTimeTrackingEnabled,
    timeTrackingDataSource,
    setTimeTrackingDataSource,
    isTimeTrackingEnabled: () => timeTrackingEnabled,
    requiresMFA,
    mfaFactorId,
    completeMFAChallenge,
    cancelMFAChallenge,
  }), [
    session,
    user,
    loading,
    isEmployee,
    isEmployeeLoading,
    employeeAccessLevel,
    signInWithGoogle,
    signUpWithGoogle,
    signInWithApple,
    signUpWithApple,
    selectedLogoutDuration,
    timeTrackingEnabled,
    timeTrackingDataSource,
    requiresMFA,
    mfaFactorId,
    completeMFAChallenge,
    cancelMFAChallenge,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
